import { authService } from './auth'
import { useAuthStore } from '../stores/auth-store'
import type { CurrencyRateHistoryRow } from '../utils/currencyRates'

// Get API base URL from environment variable
// If not set, log a warning and use empty string (will cause errors, but makes the issue visible)
const apiBaseUrl = import.meta.env.VITE_API_BASE_URL

interface RequestOptions {
  method?: string
  headers?: Record<string, string>
  body?: string
}

/** Build absolute URL for currencyfns API (custom domain basePath `currency`). */
export function resolveCurrencyApiUrl(pathAndQuery: string): string {
  const trimmed = pathAndQuery.replace(/^\//, '')
  const explicit = import.meta.env.VITE_CURRENCY_API_BASE_URL as string | undefined
  if (explicit) {
    const base = explicit.replace(/\/$/, '')
    return `${base}/${trimmed}`
  }
  const base = (apiBaseUrl || '').replace(/\/?$/, '')
  const origin = base.replace(/\/api$/i, '')
  return `${origin}/currency/${trimmed}`
}

/** Optional query params for report endpoints that support display currency conversion. */
function appendReportCurrencyParams(queryParams: URLSearchParams): void {
  try {
    const auth = useAuthStore()
    const src = auth.shopDetails?.currency
    const disp = auth.user?.currency || auth.shopDetails?.currency
    if (src) queryParams.append('source_currency', src)
    if (disp) queryParams.append('display_currency', disp)
  } catch {
    /* Pinia may be unavailable during bootstrap */
  }
}

export type DashboardMetricRef = { type: 'builtin' | 'custom'; keyOrId: string; size?: 'sm' | 'md' | 'lg' }

export interface DashboardMetricLayout {
  id?: string
  userId: string
  storeId: number
  version: number
  metrics: DashboardMetricRef[]
  createdAt?: string
  updatedAt?: string
}

export interface DashboardCustomMetric {
  id: string
  userId: string
  storeId: number
  name: string
  description?: string
  format: 'money' | 'number' | 'percent'
  expression: string
  createdAt?: string
  updatedAt?: string
}

/** Public dropship account row from GET /dropshipfns/accounts (camelCase). */
export interface DropshipAccountPublic {
  shopId: number
  provider: string
  email?: string
  printfulStoreId?: string
  printifyShopId?: string
  status: string
  createdAt: string
  updatedAt: string
}

/** GET /dropshipfns/accounts envelope (camelCase; snake_case keys tolerated when parsing). */
export interface DropshipAccountsListResponse {
  accounts: DropshipAccountPublic[]
  /** Total POD/dropship orders synced for this shop (aggregate). */
  totalOrdersSynced: number
  /** Share of orders with reliable cost data, as a percentage e.g. 45.67 meaning 45.67%. */
  costCoverage: number
}

/** POST /dropshipfns/connect body (camelCase). shopId ignored when using API Gateway. */
export interface ConnectDropshipRequest {
  shopId?: number
  provider: string
  /** Not used for Printful (OAuth start). Required for gelato, Printify; for CJ only with legacy password (not CJ API key). */
  apiKey?: string
  email?: string
  printfulStoreId?: string
  printifyShopId?: string
}

/** Printful OAuth start: same endpoint returns this object instead of `"success"`. */
export interface PrintfulDropshipConnectStartResponse {
  authUrl: string
  state?: string
}

export interface PrintifyShop {
  id: number
  title: string
  salesChannel: string
}

/** Printify connect step 1: when printifyShopId is omitted, server returns shops list. */
export interface PrintifyConnectShopsResponse {
  shops: PrintifyShop[]
}

export type ConnectDropshipResponse = string | PrintfulDropshipConnectStartResponse | PrintifyConnectShopsResponse

export interface DropshipSyncRequestBody {
  provider: string
}

class ApiService {
  /** Convert a currency-unit value (e.g. 1.00) to minor units (e.g. 100). */
  private toMinorUnits(value: unknown): number {
    const n = typeof value === 'number' ? value : typeof value === 'string' ? parseFloat(value) : NaN
    if (!Number.isFinite(n)) return 0
    return Math.round(n * 100)
  }

  private getAuthHeaders(endpoint?: string): Record<string, string> {
    const bearerToken = authService.getBearerToken()
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    }

    // Attach auth header
    if (bearerToken) {
      headers['Authorization'] = bearerToken
    }

    // User-level endpoints should NOT have x-shop-id header
    const isUserLevelEndpoint =
      endpoint &&
      (endpoint.startsWith('/users/auth/') || endpoint.startsWith('/users/me') || endpoint.startsWith('/me/'))

    // Attach shop context header only for shop-level endpoints
    if (!isUserLevelEndpoint) {
      // Get shop ID using authService which handles:
      // 1. shop_id from token (for shop tokens)
      // 2. selected_store from localStorage (for user tokens)
      // 3. shop_id from localStorage (fallback)
      const shopId = authService.getShopId()
      if (shopId !== null) {
        headers['x-shop-id'] = shopId.toString()
      }
    }

    return headers
  }

  private async request(url: string, options: RequestOptions = {}): Promise<any> {
    // Extract endpoint path from full URL for header logic
    // Use URL.pathname for reliable path extraction (handles different base URLs)
    let endpoint: string
    try {
      const urlObj = new URL(url)
      endpoint = urlObj.pathname
    } catch {
      // Fallback if URL parsing fails (shouldn't happen, but safe)
      endpoint = url.replace(apiBaseUrl, '')
    }

    const config: RequestInit = {
      method: options.method || 'GET',
      headers: {
        ...this.getAuthHeaders(endpoint),
        ...options.headers,
      },
    }

    if (options.body) {
      config.body = options.body
    }

    // Add timeout to prevent hanging requests
    // Use shorter timeout for stores endpoint (5s) since it's called frequently
    const timeoutMs = endpoint.includes('/stores') ? 5000 : 30000

    try {
      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), timeoutMs)

      const response = await fetch(url, {
        ...config,
        signal: controller.signal,
      })

      clearTimeout(timeoutId)

      if (response.status === 401 || response.status === 403) {
        // Token expired, invalid, or insufficient permissions
        // Try to extract error message from response body
        let errorMessage = 'Authentication required'
        try {
          const errorData = await response.json()
          errorMessage = errorData.error || errorData.message || errorMessage
        } catch (parseError) {
          // If JSON parsing fails, use default message
        }

        authService.logout()

        // For shop details endpoint specifically, don't redirect immediately
        // Let the caller handle the error response
        if (url.includes('/shops/details')) {
          throw new Error(errorMessage)
        }

        // For other endpoints, redirect to auth
        authService.redirectToShopifyAuth()
        throw new Error(errorMessage)
      }

      if (!response.ok) {
        let errorData: Record<string, unknown> | null = null
        try {
          const text = await response.text()
          if (text) {
            try {
              errorData = JSON.parse(text) as Record<string, unknown>
            } catch {
              errorData = { message: text }
            }
          }
        } catch {
          errorData = null
        }

        const status = response.status
        const apiMessage =
          (typeof errorData?.message === 'string' && errorData.message) ||
          (typeof errorData?.error === 'string' && errorData.error) ||
          ''
        const apiDetails = typeof errorData?.details === 'string' ? errorData.details : ''

        let displayMessage = ''
        if (apiDetails) {
          displayMessage = apiMessage ? `${apiMessage}: ${apiDetails}` : apiDetails
        } else if (apiMessage) {
          displayMessage = apiMessage
        } else {
          displayMessage = `HTTP error! status: ${status}`
        }

        const err = new Error(displayMessage) as Error & {
          status?: number
          apiMessage?: string
          apiDetails?: string
          apiBody?: Record<string, unknown> | null
        }
        err.status = status
        err.apiMessage = apiMessage || undefined
        err.apiDetails = apiDetails || undefined
        err.apiBody = errorData
        throw err
      }

      return await response.json()
    } catch (error: any) {
      // Handle timeout/abort errors
      if (error.name === 'AbortError' || error.name === 'TimeoutError') {
        console.error(`API request timed out after ${timeoutMs}ms:`, url)
        throw new Error(`Request timeout: The server did not respond within ${timeoutMs / 1000} seconds`)
      }
      console.error('API request failed:', error)
      throw error
    }
  }

  // GET requests
  async get(endpoint: string): Promise<any> {
    return this.request(`${apiBaseUrl}${endpoint}`)
  }

  // POST requests
  async post(endpoint: string, data: any): Promise<any> {
    return this.request(`${apiBaseUrl}${endpoint}`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  // PUT requests
  async put(endpoint: string, data: any): Promise<any> {
    return this.request(`${apiBaseUrl}${endpoint}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }

  // DELETE requests
  async delete(endpoint: string): Promise<any> {
    return this.request(`${apiBaseUrl}${endpoint}`, {
      method: 'DELETE',
    })
  }

  // PATCH requests
  async patch(endpoint: string, data: any): Promise<any> {
    return this.request(`${apiBaseUrl}${endpoint}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  }

  // Order cost update endpoint
  async updateOrderCost(costType: string): Promise<any> {
    return this.post('/order-cost/update', { cost_type: costType })
  }

  // Legacy URL methods (for backwards compatibility)
  allProjects = () => `${apiBaseUrl}/projects`
  project = (id: string) => `${apiBaseUrl}/projects/${id}`
  projects = ({ page, pageSize }: { page: number; pageSize: number }) =>
    `${apiBaseUrl}/projects/?page=${page}&pageSize=${pageSize}`

  // New methods using authenticated requests
  async getProjects(page: number = 1, pageSize: number = 10): Promise<any> {
    return this.get(`/projects?page=${page}&pageSize=${pageSize}`)
  }

  async getProject(id: string): Promise<any> {
    return this.get(`/projects/${id}`)
  }

  async createProject(data: any): Promise<any> {
    return this.post('/projects', data)
  }

  async updateProject(id: string, data: any): Promise<any> {
    return this.put(`/projects/${id}`, data)
  }

  async deleteProject(id: string): Promise<any> {
    return this.delete(`/projects/${id}`)
  }

  // Orders API
  async getOrders(
    params: {
      page?: number
      pageSize?: number
      search?: string
      status?: string | string[]
      dateFrom?: string
      dateTo?: string
      minRevenue?: number
      maxRevenue?: number
      sortBy?: string
      sortOrder?: 'asc' | 'desc'
    } = {},
  ): Promise<any> {
    const queryParams = new URLSearchParams()

    if (params.page) queryParams.append('page', params.page.toString())
    if (params.pageSize) queryParams.append('pageSize', params.pageSize.toString())
    if (params.search) queryParams.append('search', params.search)

    // Handle multiple status values
    if (params.status) {
      if (Array.isArray(params.status)) {
        // For array of statuses, append each one as a separate status parameter
        params.status.forEach((status) => {
          queryParams.append('status', status)
        })
      } else {
        // For single status, append as before
        queryParams.append('status', params.status)
      }
    }

    if (params.dateFrom) queryParams.append('dateFrom', params.dateFrom)
    if (params.dateTo) queryParams.append('dateTo', params.dateTo)
    if (params.minRevenue !== undefined) queryParams.append('minRevenue', params.minRevenue.toString())
    if (params.maxRevenue !== undefined) queryParams.append('maxRevenue', params.maxRevenue.toString())
    if (params.sortBy) queryParams.append('sortBy', params.sortBy)
    if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder)

    const queryString = queryParams.toString()
    return this.get(`/orders${queryString ? `?${queryString}` : ''}`)
  }

  async getOrder(id: string): Promise<any> {
    return this.get(`/orders/${id}`)
  }

  async deleteOrder(id: string): Promise<any> {
    return this.delete(`/orders/${id}`)
  }

  async updateOrderStatus(id: string, status: string, trackingNumber?: string, notes?: string): Promise<any> {
    return this.patch(`/orders/${id}/status`, {
      status,
      trackingNumber,
      notes,
    })
  }

  // Cost Settings API
  async getCostSettings(): Promise<any> {
    return this.get('/cost-settings')
  }

  async updateCostSettings(data: any): Promise<any> {
    return this.put('/cost-settings', data)
  }

  // Marketing Channels API
  async getMarketingChannels(): Promise<
    Array<{
      id: string
      platformId: string
      name: string
      email: string
      connectedAt: string
      status: string
      adAccounts: any[] | null
      shopId: number
      accountId: string
      token: {
        accessToken: string
        expiresAt: string
        refreshToken: string
      }
      shopCreatedAt: string
    }>
  > {
    return this.get('/integration/marketing-channel/accounts')
  }

  async connectMarketingChannel(
    channel: string,
    data: any = {},
  ): Promise<{
    shopId: number
    error: string
    redirectUrl?: string
    account?: any
  }> {
    return this.post(`/integration/marketing-channel/${channel}/connect`, data)
  }

  async disconnectMarketingChannel(platform: string, accountId: string): Promise<any> {
    return this.delete(`/integration/marketing-channel/${platform}/${accountId}`)
  }

  async getMarketingChannelAccounts(channel: string): Promise<any> {
    return this.get(`/integration/marketing-channel/${channel}/accounts`)
  }

  /** Fetch / refresh ad accounts for a marketing connection (GET). */
  async syncAdAccounts(channel: string, accountId: string): Promise<any> {
    return this.get(`/integration/marketing-channel/${channel}/sync-ad-accounts/${accountId}`)
  }

  async syncMarketingChannel(channel: string): Promise<any> {
    return this.post(`/integration/marketing-channel/${channel}/sync`, {})
  }

  // Shipping Providers API
  async getShippingProviders(): Promise<any> {
    return this.get('/integration/shipping-providers')
  }

  async connectShippingProvider(provider: string, credentials: any): Promise<any> {
    return this.post(`/integration/shipping-providers/${provider}/connect`, credentials)
  }

  async disconnectShippingProvider(provider: string): Promise<any> {
    return this.delete(`/integration/shipping-providers/${provider}`)
  }

  async syncShippingProvider(provider: string): Promise<any> {
    return this.post(`/integration/shipping-providers/${provider}/sync`, {})
  }

  /** Dropship / POD (API Gateway base path dropshipfns). Auth: JWT + x-shop-id. */
  async getDropshipAccounts(): Promise<DropshipAccountsListResponse> {
    const data = await this.get('/dropshipfns/accounts')
    const n = (v: unknown): number => {
      if (typeof v === 'number' && Number.isFinite(v)) return v
      if (typeof v === 'string' && String(v).trim() !== '') {
        const x = Number(v)
        if (Number.isFinite(x)) return x
      }
      return 0
    }
    if (Array.isArray(data)) {
      return { accounts: data as DropshipAccountPublic[], totalOrdersSynced: 0, costCoverage: 0 }
    }
    const o = data as Record<string, unknown>
    const rawAccounts = o.accounts
    const accounts = Array.isArray(rawAccounts) ? (rawAccounts as DropshipAccountPublic[]) : []
    return {
      accounts,
      totalOrdersSynced: n(o.totalOrdersSynced ?? o['total_orders_synced']),
      costCoverage: n(o.costCoverage ?? o['cost_coverage']),
    }
  }

  /**
   * POST /dropshipfns/connect. JWT + x-shop-id as for other dropship calls.
   * Most providers return JSON `"success"`; Printful OAuth returns `{ authUrl, state? }`.
   */
  async connectDropshipAccount(payload: ConnectDropshipRequest): Promise<ConnectDropshipResponse> {
    return this.post('/dropshipfns/connect', payload)
  }

  /** Enqueue incremental provider sync for this shop (may 429 due to cooldown). */
  async requestDropshipSync(payload: DropshipSyncRequestBody): Promise<any> {
    return this.post('/dropshipfns/sync/request', payload)
  }

  /** DELETE /dropshipfns/disconnect/{provider}. Removes stored credentials for this shop. */
  async disconnectDropshipAccount(provider: string): Promise<void> {
    const p = encodeURIComponent(
      String(provider || '')
        .toLowerCase()
        .trim(),
    )
    await this.delete(`/dropshipfns/disconnect/${p}`)
  }

  async activateAdAccount(channel: string, accountId: string, adAccountId: string): Promise<any> {
    return this.post(`/integration/marketing-channel/${channel}/activate/${accountId}/${adAccountId}`, {})
  }

  async deactivateAdAccount(channel: string, accountId: string, adAccountId: string): Promise<any> {
    return this.post(`/integration/marketing-channel/${channel}/deactivate/${accountId}/${adAccountId}`, {})
  }

  // Shop API
  async getShopDetails(): Promise<any> {
    // Use user-specific endpoint when authenticated with user token
    const isUserToken = authService.isUserToken()
    const endpoint = isUserToken ? '/shops/user/details' : '/shops/details'
    return this.get(endpoint)
  }

  async completeInstall(): Promise<any> {
    return this.post('/shops/install/complete', {})
  }

  // Auth API
  async shopifyLogin(shopDomain: string, includeAuth = false): Promise<any> {
    const url = `${apiBaseUrl}/auth/shopify/login?shop=${encodeURIComponent(shopDomain)}${includeAuth ? '&include_auth=true' : ''}`

    // For this endpoint, we don't need authorization headers since it's for login
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    })

    if (!response.ok) {
      // Try to extract error message from response body for 400 and 401 errors
      if (response.status === 400 || response.status === 401) {
        try {
          const errorData = await response.json()
          const errorMessage = errorData.error || errorData.message || `HTTP error! status: ${response.status}`
          throw new Error(errorMessage)
        } catch (parseError) {
          // If JSON parsing fails, fall back to generic error
          throw new Error(`HTTP error! status: ${response.status}`)
        }
      }
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    return response.json()
  }

  // Payment Plans API
  async getPaymentPlans(): Promise<any> {
    return this.get('/payment/plans')
  }

  // Subscription API
  async createSubscription(planKey: string): Promise<any> {
    return this.post('/payment/subscriptions', { plan_key: planKey })
  }

  async activateSubscription(subscriptionId: string, couponCode?: string): Promise<any> {
    const body: any = {}
    if (couponCode) {
      body.coupon_code = couponCode
    }
    return this.post(`/payment/subscriptions/${subscriptionId}/activate`, body)
  }

  async getSubscription(): Promise<any> {
    return this.get('/payment/subscription')
  }

  // Transaction Fees API
  async getCustomGateways(): Promise<any> {
    return this.get('/transaction-fees/custom-gateways')
  }

  async getAvailableGateways(): Promise<any> {
    return this.get('/transaction-fees/available-gateways')
  }

  async createCustomGateway(gateway: {
    name: string
    description: string
    gatewayRate: number
    gatewayFixedFee: number
    shopifyExtraFee: number
  }): Promise<any> {
    return this.post('/transaction-fees/custom-gateways', {
      name: gateway.name,
      description: gateway.description,
      gatewayRate: Number(gateway.gatewayRate),
      // Backend stores fixed fees in minor units (cents)
      gatewayFixedFee: this.toMinorUnits(gateway.gatewayFixedFee),
      shopifyExtraFee: Number(gateway.shopifyExtraFee),
    })
  }

  async updateCustomGateway(
    gatewayId: number,
    gateway: {
      name: string
      description: string
      gatewayRate: number
      gatewayFixedFee: number
      shopifyExtraFee: number
    },
  ): Promise<any> {
    return this.put(`/transaction-fees/custom-gateways/${gatewayId}`, {
      name: gateway.name,
      description: gateway.description,
      gatewayRate: Number(gateway.gatewayRate),
      // Backend stores fixed fees in minor units (cents)
      gatewayFixedFee: this.toMinorUnits(gateway.gatewayFixedFee),
      shopifyExtraFee: Number(gateway.shopifyExtraFee),
    })
  }

  async deleteCustomGateway(gatewayId: number): Promise<any> {
    return this.delete(`/transaction-fees/custom-gateways/${gatewayId}`)
  }

  // Shipping Cost APIs
  async getDefaultShippingCost(): Promise<any> {
    return this.get('/shipping/default-cost')
  }

  async updateDefaultShippingCost(defaultCost: number): Promise<any> {
    return this.put('/shipping/default-cost', { defaultCost })
  }

  async getShippingZones(): Promise<any> {
    return this.get('/shipping/zones')
  }

  async createShippingZone(zone: { name: string; country_codes: string[] }): Promise<any> {
    return this.post('/shipping/zones', zone)
  }

  async updateShippingZone(zoneId: string | number, zone: { name: string; country_codes: string[] }): Promise<any> {
    return this.put(`/shipping/zones/${encodeURIComponent(String(zoneId))}`, zone)
  }

  async deleteShippingZone(zoneId: string | number): Promise<any> {
    return this.delete(`/shipping/zones/${encodeURIComponent(String(zoneId))}`)
  }

  async getShippingProfiles(params?: { zone_id?: string }): Promise<any> {
    const z = params?.zone_id
    const q = z != null && z !== '' ? `?zone_id=${encodeURIComponent(z)}` : ''
    return this.get(`/shipping/profiles${q}`)
  }

  async createShippingProfile(body: {
    name: string
    zone_id?: string | null
    is_default: boolean
    apply_to_all_products: boolean
  }): Promise<any> {
    return this.post('/shipping/profiles', body)
  }

  async updateShippingProfile(
    profileId: string | number,
    body: { name: string; zone_id?: string | null; is_default: boolean; apply_to_all_products: boolean },
  ): Promise<any> {
    return this.put(`/shipping/profiles/${encodeURIComponent(String(profileId))}`, body)
  }

  async deleteShippingProfile(profileId: string | number): Promise<any> {
    return this.delete(`/shipping/profiles/${encodeURIComponent(String(profileId))}`)
  }

  async getShippingProfileProducts(profileId: string | number): Promise<any> {
    return this.get(`/shipping/profiles/${encodeURIComponent(String(profileId))}/products`)
  }

  async assignProductsToShippingProfile(profileId: string | number, productIds: number[]): Promise<any> {
    return this.post(`/shipping/profiles/${encodeURIComponent(String(profileId))}/products/assign`, {
      product_ids: productIds,
    })
  }

  async removeProductsFromShippingProfile(profileId: string | number, productIds: number[]): Promise<any> {
    return this.post(`/shipping/profiles/${encodeURIComponent(String(profileId))}/products/remove`, {
      product_ids: productIds,
    })
  }

  async getShippingRules(params: { profile_id: string }): Promise<any> {
    const query = new URLSearchParams({ profile_id: params.profile_id }).toString()
    return this.get(`/shipping/rules?${query}`)
  }

  async createShippingRule(body: {
    zone_id: string
    profile_id: string
    calculation_type: 'quantity_based' | 'weight_based' | 'lineitem_based'
    currency: string
    conditions: {
      tiers: Array<{ min_quantity?: number; min_weight?: number; amount: number }>
    }
    cost: { type: 'fixed'; amount: number }
    effective_from: string
    effective_to?: string | null
  }): Promise<any> {
    return this.post('/shipping/rules', body)
  }

  async updateShippingRule(
    ruleId: string | number,
    body: {
      zone_id: string
      profile_id: string
      calculation_type: 'quantity_based' | 'weight_based' | 'lineitem_based'
      currency: string
      conditions: {
        tiers: Array<{ min_quantity?: number; min_weight?: number; amount: number }>
      }
      cost: { type: 'fixed'; amount: number }
      effective_from: string
      effective_to?: string | null
    },
  ): Promise<any> {
    return this.put(`/shipping/rules/${encodeURIComponent(String(ruleId))}`, body)
  }

  async deleteShippingRule(ruleId: string | number): Promise<any> {
    return this.delete(`/shipping/rules/${encodeURIComponent(String(ruleId))}`)
  }

  async getShippingCalculationSettings(): Promise<any> {
    return this.get('/shipping/calculation-settings')
  }

  async updateShippingCalculationSettings(calculationMethod: string): Promise<any> {
    return this.put('/shipping/calculation-settings', { calculationMethod })
  }

  // Custom Costs Management
  async getCustomCosts(): Promise<any> {
    return this.get('/custom-costs')
  }

  async createCustomCost(cost: {
    name: string
    description?: string
    type: string
    amount: number
    frequency: string
    startDate: string
    endDate?: string | null
    isOngoing: boolean
  }): Promise<any> {
    return this.post('/custom-costs', cost)
  }

  async updateCustomCost(
    costId: number,
    cost: {
      name: string
      description?: string
      type: string
      amount: number
      frequency: string
      startDate: string
      endDate?: string | null
      isOngoing: boolean
    },
  ): Promise<any> {
    return this.put(`/custom-costs/${costId}`, cost)
  }

  async deleteCustomCost(costId: number): Promise<any> {
    return this.delete(`/custom-costs/${costId}`)
  }

  // Data Sync Endpoints
  async syncOrders(): Promise<any> {
    return this.post('/orders/sync', {})
  }

  async syncProducts(): Promise<any> {
    return this.post('/products/sync', {})
  }

  async getSyncStatus(): Promise<any> {
    return this.get('/sync/status')
  }

  // Product Cost Management APIs
  async getGeneralCogsRate(): Promise<any> {
    return this.get('/products/general-cogs-rate')
  }

  async updateGeneralCogsRate(rate: number | string): Promise<any> {
    const numericRate = typeof rate === 'number' ? rate : Number.parseFloat(String(rate))
    return this.put('/products/general-cogs-rate', { rate: numericRate })
  }

  async getProducts(params?: {
    page?: number
    limit?: number
    search?: string
    status?: string
    costType?: string
    zone?: string
  }): Promise<any> {
    const queryParams = new URLSearchParams()
    if (params?.page) queryParams.append('page', params.page.toString())
    if (params?.limit) queryParams.append('limit', params.limit.toString())
    if (params?.search) queryParams.append('search', params.search)
    if (params?.status && params.status !== 'all') queryParams.append('status', params.status)
    if (params?.costType && params.costType !== 'all') queryParams.append('cost_type', params.costType)
    if (params?.zone && params.zone !== 'global') queryParams.append('zone', params.zone)

    const query = queryParams.toString()
    return this.get(`/products${query ? `?${query}` : ''}`)
  }

  async getProduct(productId: number, zoneId?: string): Promise<any> {
    const queryParams = new URLSearchParams()
    if (zoneId && zoneId !== 'global') {
      queryParams.append('zoneId', zoneId)
    }
    const query = queryParams.toString()
    return this.get(`/products/${productId}${query ? `?${query}` : ''}`)
  }

  async getProductZones(): Promise<any> {
    return this.get('/products/zones')
  }

  private zonePath(zoneId: string) {
    return `/products/zones/${encodeURIComponent(zoneId)}`
  }

  async createProductZone(body: { zoneName: string; countryCodes: string[] }): Promise<any> {
    return this.post('/products/zones', body)
  }

  async updateProductZone(
    zoneId: string,
    body: { countryCodes: string[]; autoIncludeNewCatalog?: boolean },
  ): Promise<any> {
    return this.put(this.zonePath(zoneId), body)
  }

  async deleteProductZone(zoneId: string): Promise<any> {
    return this.delete(this.zonePath(zoneId))
  }

  async getZoneProducts(zoneId: string): Promise<any> {
    return this.get(`${this.zonePath(zoneId)}/products`)
  }

  async assignProductsToZone(zoneId: string, productIds: number[]): Promise<any> {
    return this.post(`${this.zonePath(zoneId)}/products/assign`, { productIds })
  }

  async removeProductsFromZone(zoneId: string, productIds: number[]): Promise<any> {
    return this.post(`${this.zonePath(zoneId)}/products/remove`, { productIds })
  }

  async assignAllProductsToZone(zoneId: string): Promise<any> {
    return this.post(`${this.zonePath(zoneId)}/products/assign-all`, {})
  }

  async updateProductCogsLevel(productId: number, useVariantCogs: boolean, zoneId?: string): Promise<any> {
    const body: { use_variant_cogs: boolean; zoneId?: string } = { use_variant_cogs: useVariantCogs }
    if (zoneId && zoneId !== 'global') {
      body.zoneId = zoneId
    }
    return this.put(`/products/${productId}/cogs-level`, body)
  }

  async updateProductCogsSource(productId: number, cogsSource: 'manual' | 'shopify', zoneId?: string): Promise<any> {
    const body: { cogsSource: string; zoneId?: string } = { cogsSource }
    if (zoneId && zoneId !== 'global') {
      body.zoneId = zoneId
    }
    return this.put(`/products/${productId}/cogs-source`, body)
  }

  async bulkUpdateProductCogsSource(
    products: Array<{ product_id: number; cogsSource: 'manual' | 'shopify' }>,
    zoneId?: string,
  ): Promise<any> {
    const body: {
      products: Array<{ product_id: number; cogsSource: 'manual' | 'shopify' }>
      zoneId?: string
    } = { products }
    if (zoneId && zoneId !== 'global') {
      body.zoneId = zoneId
    }
    return this.put('/products/bulk-cogs-source', body)
  }

  async bulkUpdateProductCostType(
    products: Array<{ product_id: number; use_variant_cogs: boolean }>,
    zoneId?: string,
  ): Promise<any> {
    const body: { products: Array<{ product_id: number; use_variant_cogs: boolean }>; zoneId?: string } = { products }
    if (zoneId && zoneId !== 'global') {
      body.zoneId = zoneId
    }
    return this.put('/products/bulk-cogs-level', body)
  }

  async bulkUpdateCogsVersion(data: {
    shop_created_at: string
    items: Array<{
      product_id: number
      variant_id: number
      tiers: Array<{ min_qty: number; max_qty: number | null; unit_cost: number }>
    }>
    zoneId?: string
  }): Promise<any> {
    if (data.zoneId && data.zoneId === 'global') {
      const rest = { ...data }
      delete rest.zoneId
      return this.put('/products/bulk-cogs-version-update', rest)
    }
    return this.put('/products/bulk-cogs-version-update', data)
  }

  async updateProductBaseCogs(productId: number, cogs: number, zoneId?: string): Promise<any> {
    const body: { cogs: number; zoneId?: string } = { cogs }
    if (zoneId && zoneId !== 'global') {
      body.zoneId = zoneId
    }
    return this.put(`/products/${productId}/cogs`, body)
  }

  async updateVariantBaseCogs(productId: number, variantId: number, cogs: number, zoneId?: string): Promise<any> {
    const body: { cogs: number; zoneId?: string } = { cogs }
    if (zoneId && zoneId !== 'global') {
      body.zoneId = zoneId
    }
    return this.put(`/products/${productId}/variants/${variantId}/cogs`, body)
  }

  async getProductCogsVersions(productId: number): Promise<any> {
    return this.get(`/products/${productId}/cogs-versions`)
  }

  async createProductCogsVersion(productId: number, versionData: any, zoneId?: string): Promise<any> {
    if (zoneId && zoneId !== 'global') {
      return this.post(`/products/${productId}/cogs-versions`, { zoneId, versions: versionData })
    }
    return this.post(`/products/${productId}/cogs-versions`, versionData)
  }

  async updateProductCogsVersion(productId: number, versionId: string, versionData: any): Promise<any> {
    return this.put(`/products/${productId}/cogs-versions/${versionId}`, versionData)
  }

  async deleteProductCogsVersion(productId: number, versionId: string): Promise<any> {
    return this.delete(`/products/${productId}/cogs-versions/${versionId}`)
  }

  async getVariantCogsVersions(productId: number, variantId: number): Promise<any> {
    return this.get(`/products/${productId}/variants/${variantId}/cogs-versions/`)
  }

  async createVariantCogsVersion(
    productId: number,
    variantId: number,
    versionData: any,
    zoneId?: string,
  ): Promise<any> {
    if (zoneId && zoneId !== 'global') {
      return this.post(`/products/${productId}/variants/${variantId}/cogs-versions/`, { zoneId, versions: versionData })
    }
    return this.post(`/products/${productId}/variants/${variantId}/cogs-versions/`, versionData)
  }

  async updateVariantCogsVersion(
    productId: number,
    variantId: number,
    versionId: string,
    versionData: any,
  ): Promise<any> {
    return this.put(`/products/${productId}/variants/${variantId}/cogs-versions/${versionId}`, versionData)
  }

  async deleteVariantCogsVersion(productId: number, variantId: number, versionId: string): Promise<any> {
    return this.delete(`/products/${productId}/variants/${variantId}/cogs-versions/${versionId}`)
  }

  async getProductCogsHistory(
    productId: number,
    params?: {
      start_date?: string
      end_date?: string
      limit?: number
    },
  ): Promise<any> {
    const queryParams = new URLSearchParams()
    if (params?.start_date) queryParams.append('start_date', params.start_date)
    if (params?.end_date) queryParams.append('end_date', params.end_date)
    if (params?.limit) queryParams.append('limit', params.limit.toString())

    const query = queryParams.toString()
    return this.get(`/products/${productId}/cogs-history${query ? `?${query}` : ''}`)
  }

  // Shipping Products API
  async getShippingProducts(params?: { page?: number; limit?: number; search?: string }): Promise<any> {
    const queryParams = new URLSearchParams()
    if (params?.page) queryParams.append('page', params.page.toString())
    if (params?.limit) queryParams.append('limit', params.limit.toString())
    if (params?.search) queryParams.append('search', params.search)

    const query = queryParams.toString()
    return this.get(`/products/shipping${query ? `?${query}` : ''}`)
  }

  // Customers API
  async getCustomerOverview(): Promise<any> {
    return this.get('/customers/overview')
  }

  async getCustomerInsights(params: {
    from: string
    to: string
    country?: string
    source?: string
    discount_code?: string
  }): Promise<any> {
    const queryParams = new URLSearchParams()
    queryParams.append('from', params.from)
    queryParams.append('to', params.to)
    if (params.country) queryParams.append('country', params.country)
    if (params.source) queryParams.append('source', params.source)
    if (params.discount_code) queryParams.append('discount_code', params.discount_code)
    return this.get(`/customers/insights?${queryParams.toString()}`)
  }

  async getCustomerDiscountCodes(params: {
    from: string
    to: string
    country?: string
    source?: string
    discount_code?: string
    financialStatus?: string[]
  }): Promise<any> {
    const queryParams = new URLSearchParams()
    queryParams.append('from', params.from)
    queryParams.append('to', params.to)
    if (params.country) queryParams.append('country', params.country)
    if (params.source) queryParams.append('source', params.source)
    if (params.discount_code) queryParams.append('discount_code', params.discount_code)
    ;(params.financialStatus || []).forEach((s) => queryParams.append('financialStatus', s))
    return this.get(`/customers/discount-codes?${queryParams.toString()}`)
  }

  async getCustomerSources(params: {
    from: string
    to: string
    country?: string
    source?: string
    discount_code?: string
    financialStatus?: string[]
  }): Promise<any> {
    const queryParams = new URLSearchParams()
    queryParams.append('from', params.from)
    queryParams.append('to', params.to)
    if (params.country) queryParams.append('country', params.country)
    if (params.source) queryParams.append('source', params.source)
    if (params.discount_code) queryParams.append('discount_code', params.discount_code)
    ;(params.financialStatus || []).forEach((s) => queryParams.append('financialStatus', s))
    return this.get(`/customers/sources?${queryParams.toString()}`)
  }

  // Dashboard API
  async getDashboardData(params?: { start_date?: string; end_date?: string; period?: string }): Promise<any> {
    const queryParams = new URLSearchParams()
    if (params?.start_date) queryParams.append('start_date', params.start_date)
    if (params?.end_date) queryParams.append('end_date', params.end_date)
    if (params?.period) queryParams.append('period', params.period)
    appendReportCurrencyParams(queryParams)

    const query = queryParams.toString()
    return this.get(`/reports/dashboard${query ? `?${query}` : ''}`)
  }

  /** Dashboard aggregated across all stores (no x-shop-id). Same shape as getDashboardData plus stores[]. */
  async getDashboardAllStoreData(params?: { start_date?: string; end_date?: string; period?: string }): Promise<any> {
    const queryParams = new URLSearchParams()
    if (params?.start_date) queryParams.append('start_date', params.start_date)
    if (params?.end_date) queryParams.append('end_date', params.end_date)
    if (params?.period) queryParams.append('period', params.period)
    appendReportCurrencyParams(queryParams)

    const query = queryParams.toString()
    return this.get(`/reports/dashboard-all-store${query ? `?${query}` : ''}`)
  }

  // Dashboard metric customization APIs
  async getDashboardMetricLayout(): Promise<DashboardMetricLayout> {
    return this.get('/reports/dashboard/metric-layout')
  }

  async saveDashboardMetricLayout(payload: {
    version?: number
    metrics: DashboardMetricRef[]
  }): Promise<DashboardMetricLayout> {
    return this.put('/reports/dashboard/metric-layout', payload)
  }

  async resetDashboardMetricLayout(): Promise<{ success: boolean }> {
    return this.post('/reports/dashboard/metric-layout/reset', {})
  }

  async listDashboardCustomMetrics(): Promise<{ metrics: DashboardCustomMetric[] }> {
    return this.get('/reports/dashboard/custom-metrics')
  }

  async createDashboardCustomMetric(payload: {
    name: string
    description?: string
    format: 'money' | 'number' | 'percent'
    expression: string
  }): Promise<DashboardCustomMetric> {
    return this.post('/reports/dashboard/custom-metrics', payload)
  }

  async updateDashboardCustomMetric(
    metricId: string,
    payload: {
      name: string
      description?: string
      format: 'money' | 'number' | 'percent'
      expression: string
    },
  ): Promise<DashboardCustomMetric> {
    return this.put(`/reports/dashboard/custom-metrics/${encodeURIComponent(metricId)}`, payload)
  }

  async deleteDashboardCustomMetric(metricId: string): Promise<{ success: boolean }> {
    return this.delete(`/reports/dashboard/custom-metrics/${encodeURIComponent(metricId)}`)
  }

  // Email Reports API
  async getEmailReports(): Promise<any> {
    return this.get('/reports/email-reports')
  }

  async createEmailReport(config: any): Promise<any> {
    return this.post('/reports/email-reports', config)
  }

  async updateEmailReport(id: string, config: any): Promise<any> {
    return this.put(`/reports/email-reports/${id}`, config)
  }

  async deleteEmailReport(id: string): Promise<any> {
    return this.delete(`/reports/email-reports/${id}`)
  }

  async toggleEmailReport(id: string, isActive: boolean): Promise<any> {
    return this.patch(`/reports/email-reports/${id}/toggle`, { is_active: isActive })
  }

  // Profit & Loss API
  async getProfitLossData(params?: { start_date?: string; end_date?: string; period?: string }): Promise<any> {
    const queryParams = new URLSearchParams()
    if (params?.start_date) queryParams.append('start_date', params.start_date)
    if (params?.end_date) queryParams.append('end_date', params.end_date)
    if (params?.period) queryParams.append('period', params.period)
    appendReportCurrencyParams(queryParams)

    const query = queryParams.toString()
    return this.get(`/reports/profit-loss${query ? `?${query}` : ''}`)
  }

  // Profit & Loss API (All Stores)
  async getProfitLossAllStoreData(params?: { start_date?: string; end_date?: string; period?: string }): Promise<any> {
    const queryParams = new URLSearchParams()
    if (params?.start_date) queryParams.append('start_date', params.start_date)
    if (params?.end_date) queryParams.append('end_date', params.end_date)
    if (params?.period) queryParams.append('period', params.period)
    appendReportCurrencyParams(queryParams)

    const query = queryParams.toString()
    return this.get(`/reports/profit-loss-all-store${query ? `?${query}` : ''}`)
  }

  // Product Performance API
  /** GET currency exchange snapshots (OpenExchangeRates-style), for frontend conversion. */
  async getCurrencyRatesHistory(params?: { date?: string }): Promise<CurrencyRateHistoryRow[]> {
    const q = new URLSearchParams()
    if (params?.date) q.set('date', params.date)
    const qs = q.toString()
    const url = resolveCurrencyApiUrl(`rates-history${qs ? `?${qs}` : ''}`)
    return this.request(url)
  }

  async getProductsOverview(params?: { start_date?: string; end_date?: string; period?: string }): Promise<any> {
    const queryParams = new URLSearchParams()
    if (params?.start_date) queryParams.append('start_date', params.start_date)
    if (params?.end_date) queryParams.append('end_date', params.end_date)
    if (params?.period) queryParams.append('period', params.period)

    const query = queryParams.toString()
    return this.get(`/reports/products/overview${query ? `?${query}` : ''}`)
  }

  async getTopRevenueProducts(params?: { start_date?: string; end_date?: string; period?: string }): Promise<any> {
    const queryParams = new URLSearchParams()
    if (params?.start_date) queryParams.append('start_date', params.start_date)
    if (params?.end_date) queryParams.append('end_date', params.end_date)
    if (params?.period) queryParams.append('period', params.period)

    const query = queryParams.toString()
    return this.get(`/reports/products/top-revenue${query ? `?${query}` : ''}`)
  }

  async getBundleOpportunity(params?: { start_date?: string; end_date?: string; period?: string }): Promise<any> {
    const queryParams = new URLSearchParams()
    if (params?.start_date) queryParams.append('start_date', params.start_date)
    if (params?.end_date) queryParams.append('end_date', params.end_date)
    if (params?.period) queryParams.append('period', params.period)

    const query = queryParams.toString()
    return this.get(`/reports/products/bundle-opportunity${query ? `?${query}` : ''}`)
  }

  async getProductsBreakdown(params?: {
    from?: string
    to?: string
    status?: string
    q?: string
    searchMode?: string
    sortBy?: string
    sortDir?: number
    limit?: number
    cursor?: string
    offset?: number
    includeZeroSales?: boolean
  }): Promise<any> {
    const queryParams = new URLSearchParams()
    if (params?.from) queryParams.append('from', params.from)
    if (params?.to) queryParams.append('to', params.to)
    if (params?.status) queryParams.append('status', params.status)
    if (params?.q) queryParams.append('q', params.q)
    if (params?.searchMode) queryParams.append('searchMode', params.searchMode)
    if (params?.sortBy) queryParams.append('sortBy', params.sortBy)
    if (params?.sortDir !== undefined) queryParams.append('sortDir', params.sortDir.toString())
    if (params?.limit) queryParams.append('limit', params.limit.toString())
    if (params?.cursor) queryParams.append('cursor', params.cursor)
    if (params?.offset !== undefined) queryParams.append('offset', params.offset.toString())
    if (params?.includeZeroSales !== undefined) {
      queryParams.append('includeZeroSales', params.includeZeroSales.toString())
    }

    const query = queryParams.toString()
    return this.get(`/reports/products-performance${query ? `?${query}` : ''}`)
  }

  // Validate coupon
  async validateCoupon(couponCode: string): Promise<any> {
    return this.get(`/payment/coupons/validate?coupon_code=${encodeURIComponent(couponCode)}`)
  }

  // Apply coupon to subscription
  async applyCouponToSubscription(couponCode: string): Promise<any> {
    return this.post('/payment/subscriptions/apply-coupon', {
      coupon_code: couponCode,
    })
  }

  // User System API
  async login(data: { email: string; password: string }): Promise<any> {
    const url = `${apiBaseUrl}/users/auth/login`
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: `HTTP error! status: ${response.status}` }))
      const errorMessage = error.error || error.message || `HTTP error! status: ${response.status}`
      throw new Error(errorMessage)
    }

    return response.json()
  }

  async signup(data: { name?: string; email: string; password: string }): Promise<any> {
    const url = `${apiBaseUrl}/users/auth/signup`
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: `HTTP error! status: ${response.status}` }))
      const errorMessage = error.error || error.message || `HTTP error! status: ${response.status}`
      throw new Error(errorMessage)
    }

    return response.json()
  }

  async getCurrentUser(): Promise<any> {
    return this.get('/users/auth/me')
  }

  async updateUser(data: {
    name?: string
    email?: string
    timezone?: string
    currency?: string
    default_date_range?: string
    theme?: string
  }): Promise<any> {
    return this.put('/users/auth/me', data)
  }

  async changePassword(data: { current_password: string; new_password: string }): Promise<any> {
    return this.put('/users/auth/password', data)
  }

  async getUserStores(): Promise<any> {
    return this.get('/users/me/stores')
  }

  async linkStore(data: { shop_jwt: string }): Promise<any> {
    // POST /users/me/stores/link - Link store using shop JWT
    // Requires user JWT token in Authorization header
    return this.post('/users/me/stores/link', data)
  }

  async unlinkStore(storeId: number): Promise<any> {
    // DELETE /users/me/stores - Remove store from user account
    // Requires user JWT token in Authorization header
    // store_id must be sent in request body, not as path parameter
    return this.request(`${apiBaseUrl}/users/me/stores`, {
      method: 'DELETE',
      body: JSON.stringify({ store_id: storeId }),
    })
  }

  async getShopDetailsWithToken(shopToken: string): Promise<any> {
    const url = `${apiBaseUrl}/shops/details`
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${shopToken}`,
      },
    })

    if (!response.ok) {
      // Try to extract error message from response body for 400 and 401 errors
      if (response.status === 400 || response.status === 401) {
        try {
          const errorData = await response.json()
          const errorMessage = errorData.error || errorData.message || `HTTP error! status: ${response.status}`
          throw new Error(errorMessage)
        } catch (parseError) {
          // If JSON parsing fails, fall back to generic error
          throw new Error(`HTTP error! status: ${response.status}`)
        }
      }
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    return response.json()
  }

  // Auto-Match API
  async listAutoMatchSuggestions(params?: {
    channel?: string
    status?: string
    min_confidence?: number
    max_confidence?: number
    match_basis?: string
    q?: string
    page?: number
    page_size?: number
  }): Promise<any> {
    const queryParams = new URLSearchParams()
    if (params?.channel) queryParams.append('channel', params.channel)
    if (params?.status) queryParams.append('status', params.status)
    if (params?.min_confidence) queryParams.append('min_confidence', params.min_confidence.toString())
    if (params?.max_confidence) queryParams.append('max_confidence', params.max_confidence.toString())
    if (params?.match_basis) queryParams.append('match_basis', params.match_basis)
    if (params?.q) queryParams.append('q', params.q)
    if (params?.page) queryParams.append('page', params.page.toString())
    if (params?.page_size) queryParams.append('page_size', params.page_size.toString())

    const query = queryParams.toString()
    return this.get(`/reports/auto-match/suggestions${query ? `?${query}` : ''}`)
  }

  async runAutoMatch(data: { channel: 'all' | string; days_back?: number; suggestion_version?: number }): Promise<any> {
    return this.post('/reports/auto-match/run', data)
  }

  async approveAutoMatchSuggestions(data: {
    ids?: string[]
    approve_all?: boolean
    filters?: {
      channel?: string
      match_basis?: string
      min_confidence?: number
      max_confidence?: number
    }
  }): Promise<any> {
    return this.post('/reports/auto-match/suggestions/approve', data)
  }

  async rejectAutoMatchSuggestions(data: {
    ids?: string[]
    approve_all?: boolean
    filters?: {
      channel?: string
      match_basis?: string
      min_confidence?: number
      max_confidence?: number
    }
  }): Promise<any> {
    return this.post('/reports/auto-match/suggestions/reject', data)
  }

  async applyAutoMatchSuggestions(data: { channel: string }): Promise<any> {
    return this.post('/reports/auto-match/suggestions/apply', data)
  }

  // Campaign Attribution API
  async getAvailableCampaigns(
    productId: number,
    params?: {
      channel?: string
      q?: string
      page?: number
      page_size?: number
    },
  ): Promise<any> {
    const queryParams = new URLSearchParams()
    if (params?.channel) queryParams.append('channel', params.channel)
    if (params?.q) queryParams.append('q', params.q)
    if (params?.page) queryParams.append('page', params.page.toString())
    if (params?.page_size) queryParams.append('page_size', params.page_size.toString())

    const query = queryParams.toString()
    return this.get(`/reports/link-campaigns/${productId}${query ? `?${query}` : ''}`)
  }

  async getCampaignAttributionProducts(params?: {
    page?: number
    page_size?: number
    status?: 'active' | 'all'
    q?: string
    unlinked?: boolean
    channel?: string
    as_of?: string
  }): Promise<any> {
    const queryParams = new URLSearchParams()
    if (params?.page) queryParams.append('page', params.page.toString())
    if (params?.page_size) queryParams.append('page_size', params.page_size.toString())
    if (params?.status) queryParams.append('status', params.status)
    if (params?.q) queryParams.append('q', params.q)
    if (params?.unlinked !== undefined) queryParams.append('unlinked', params.unlinked.toString())
    if (params?.channel) queryParams.append('channel', params.channel)
    if (params?.as_of) queryParams.append('as_of', params.as_of)

    const query = queryParams.toString()
    return this.get(`/reports/campaign-attribution/products${query ? `?${query}` : ''}`)
  }

  // Campaign Product Links API (for managing attribution links)
  async getCampaignProductLinks(params?: {
    product_id?: number
    campaign_id?: string
    channel?: string
    page?: number
    page_size?: number
  }): Promise<any> {
    const queryParams = new URLSearchParams()
    if (params?.product_id) queryParams.append('product_id', params.product_id.toString())
    if (params?.campaign_id) queryParams.append('campaign_id', params.campaign_id)
    if (params?.channel) queryParams.append('channel', params.channel)
    if (params?.page) queryParams.append('page', params.page.toString())
    if (params?.page_size) queryParams.append('page_size', params.page_size.toString())

    const query = queryParams.toString()
    return this.get(`/reports/campaign-product-links${query ? `?${query}` : ''}`)
  }

  /**
   * Set campaign links for a product (POST /link-campaigns/{productId}).
   * Sends full selected set; server computes add/remove.
   */
  async setProductCampaignLinks(
    productId: number,
    data: {
      channel?: string
      selected: Array<{ channel: string; campaign_id: string }>
      default_attribution_method?: string
      effective_from?: string
    },
  ): Promise<{ added?: string[]; removed?: string[]; active?: string[] }> {
    return this.post(`/reports/link-campaigns/${productId}`, data)
  }

  async updateCampaignProductLink(
    linkId: string,
    data: {
      attribution_method?: string
      manual_spend?: number
    },
  ): Promise<any> {
    return this.put(`/reports/campaign-product-links/${linkId}`, data)
  }

  async deleteCampaignProductLink(linkId: string): Promise<any> {
    return this.request(`${apiBaseUrl}/reports/campaign-product-links/${linkId}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    })
  }
}

export const apiService = new ApiService()
export default apiService
